import {inject, Injectable} from '@angular/core';
import {BloggerModalApiService} from "./blogger-modal-api.service";
import {BloggerCanBeUnbindParams} from "../types/blogger-card.types";
import {
  ColumnPropertyLazySelect,
  GetSelectDataParams,
  RecordData,
  RecordPropertyData,
  RecordUpdateRequestParams,
  SelectData
} from "../../../../types/records-table.types";
import {BloggerDeduplicationProps, BloggerSearchDuplicatesProps} from "../types/blogger-deduplication-modal.types";
import {CreateProfileBloggerParams} from "../types/create-profile-blogger-modal.types";
import {MassOffersParams} from "../types/blogger-mass-offer-modal.types";
import {RecordTableApiService} from "../../../../services-api/record-table-api.service";
import {BaseModalApiService} from "../../services/base-modal-api.service";
import {GetRecordPropertyDataByIdParams} from "../../base-modal/types/base-modal.types";
import {AttachProps, DetachProps} from "../../../../types/attach-detach.types";

@Injectable({
  providedIn: 'root'
})
export class BloggerModalService {
  private bloggerModalApiService: BloggerModalApiService = inject(BloggerModalApiService);
  private recordTableApiService: RecordTableApiService = inject(RecordTableApiService);
  private baseModalApiService: BaseModalApiService = inject(BaseModalApiService);

  public getBloggerUnbindStatus(bloggerCanBeUnbindParams: BloggerCanBeUnbindParams): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.bloggerModalApiService.isBloggerCanBeUnbind(bloggerCanBeUnbindParams).subscribe({
        next: (value) => resolve(value),
        error: (error) => reject(error)
      })
    });
  }

  public getBloggerDataById(id: number): Promise<RecordData> {
    return new Promise((resolve, reject) => {
      this.bloggerModalApiService.getBloggerCard(id).subscribe({
        next: (recordPropertyData: RecordPropertyData) => resolve(recordPropertyData),
        error: (error) => reject(error)
      })
    });
  }

  public getSearchBloggers(searchValue: string): Promise<Array<RecordData>> {
    return new Promise((resolve, reject) => {
      this.bloggerModalApiService.searchBloggers(searchValue).subscribe({
        next: (recordsTableData: Array<RecordData>) => resolve(recordsTableData),
        error: (error) => reject(error)
      })
    })
  }

  public async bloggerSearchDuplicates(bloggerSearchDuplicatesProps: BloggerSearchDuplicatesProps): Promise<Array<RecordData>> {
    return new Promise((resolve, reject) => {
      this.bloggerModalApiService.searchDuplicates(bloggerSearchDuplicatesProps).subscribe({
        next: (bloggersData: Array<RecordData>) => resolve(bloggersData),
        error: (error) => reject(error)
      });
    });
  }

  public async bloggerDeduplication(bloggerDeduplicationProps: BloggerDeduplicationProps): Promise<void> {
    return new Promise((resolve, reject) => {
      this.bloggerModalApiService.deduplicationBlogger(bloggerDeduplicationProps).subscribe({
        next: () => resolve(),
        error: (error) => reject(error)
      });
    });
  }

  public async updateRating(id: number): Promise<void> {
    return new Promise((resolve, reject) => {
      this.bloggerModalApiService.updateRating(id).subscribe({
        next: () => resolve(),
        error: (error) => reject(error)
      });
    });
  }

  public createProfileBlogger(createProfileBloggerParams: CreateProfileBloggerParams): Promise<RecordData> {
    return new Promise((resolve, reject) => {
      this.bloggerModalApiService.createProfileBlogger(createProfileBloggerParams).subscribe({
        next: (bloggerProfile) => resolve(bloggerProfile),
        error: (error) => reject(error)
      });
    });
  }

  public addNewBloggerProfile(path: string, recordData: RecordData): Promise<RecordData> {
    return new Promise((resolve, reject) => {
      this.recordTableApiService.addNewRecord(path, recordData).subscribe({
        next: (recordData: RecordData) => resolve(recordData),
        error: (error) => reject(error)
      });
    });
  }

  public deleteProfileBlogger(id: number): Promise<void> {
    return new Promise((resolve, reject) => {
      this.bloggerModalApiService.deleteProfileBlogger(id).subscribe({
        next: () => resolve(),
        error: (error) => reject(error)
      });
    });
  }

  public massOffers(massOfferParams: MassOffersParams): Promise<void> {
    return new Promise((resolve, reject) => {
      this.bloggerModalApiService.massOffers(massOfferParams).subscribe({
        next: () => resolve(),
        error: (error) => reject(error)
      })
    })
  }

  public getSelectData(propertyLazySelect: ColumnPropertyLazySelect): Promise<Array<SelectData>> {
    const getSelectDataParams: GetSelectDataParams = {
      propertyLazySelect: propertyLazySelect,
    };

    return new Promise((resolve, reject) => {
      this.recordTableApiService.fetchSelectData(getSelectDataParams).subscribe({
        next: (selectData: Array<SelectData>) => resolve(selectData),
        error: (error) => reject(error)
      });
    });
  }

  public getRecordPropertyDataById<T>(getRecordPropertyDataByIdParams: GetRecordPropertyDataByIdParams): Promise<T> {
    return new Promise((resolve, reject) => {
      this.baseModalApiService.fetchDataById<T>(getRecordPropertyDataByIdParams).subscribe({
        next: (recordPropertyData: T) => resolve(recordPropertyData),
        error: (error) => reject(error)
      });
    });
  }

  public updatedRecordField<T>(recordUpdateRequestParams: RecordUpdateRequestParams): Promise<T> {
    return new Promise((resolve, reject) => {
      this.recordTableApiService.updateRecord<T>(recordUpdateRequestParams).subscribe({
        next: (recordData: T) => resolve(recordData),
        error: (error) => reject(error)
      });
    });
  }

  public attachRecordData(attachProps: AttachProps): Promise<void> {
    return new Promise((resolve, reject) => {
      this.baseModalApiService.attachRecordData(attachProps).subscribe({
        next: () => resolve(),
        error: (error) => reject(error)
      });
    });
  }

  public detachRecordData(detachProps: DetachProps): Promise<void> {
    return new Promise((resolve, reject) => {
      this.baseModalApiService.detachRecordData(detachProps).subscribe({
        next: () => resolve(),
        error: (error) => reject(error)
      });
    });
  }

  public toggleBlacklistBlogger(path: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.bloggerModalApiService.toggleBlacklistBlogger(path).subscribe({
        next: () => resolve(),
        error: (error) => reject(error)
      });
    });
  }
}
